import type { Sample } from '../data/types';
import { PoseOverlay } from './PoseOverlay';
import { useObjectUrl } from './useObjectUrl';
import './SampleThumb.css';

export interface SampleThumbProps {
  sample: Sample;
  index: number;
  onDelete: (id: string) => void;
  /** Optional click handler — opens the sample modal with prev/next /
   *  reassign / delete affordances. Defaults to no-op so existing
   *  callers keep working. */
  onOpen?: (id: string) => void;
}

export function SampleThumb({ sample, index, onDelete, onOpen }: SampleThumbProps): JSX.Element {
  const url = useObjectUrl(sample.blob);
  const openable = Boolean(onOpen);
  // Pose samples carry their MoveNet keypoints; everything else gets
  // a plain image. The overlay is scaled down for the thumbnail
  // (smaller dots / lines) so it doesn't bury the photo at 80px.
  const keypoints = sample.poseKeypoints;
  const imageContent = url ? (
    <span className="bl-thumb__image-wrap">
      <img className="bl-thumb__image" src={url} alt={`Sample ${index + 1}`} draggable={false} />
      {keypoints && keypoints.length > 0 && <PoseOverlay keypoints={keypoints} dotRadius={0.04} />}
    </span>
  ) : (
    <span className="bl-thumb__placeholder" aria-hidden="true">
      •••
    </span>
  );
  return (
    <div className="bl-thumb">
      {openable ? (
        <button
          type="button"
          className="bl-thumb__open"
          onClick={() => onOpen?.(sample.id)}
          aria-label={`Open sample ${index + 1}`}
        >
          {imageContent}
        </button>
      ) : (
        imageContent
      )}
      <button
        type="button"
        className="bl-thumb__delete"
        onClick={() => onDelete(sample.id)}
        aria-label={`Delete sample ${index + 1}`}
      >
        ×
      </button>
    </div>
  );
}
